/**
 * Login — page de connexion unifiee par email.
 *
 * Remplace les anciens CallerLogin (login par username) et ClientLogin
 * (login par email viewer) qui forcaient l'utilisateur a choisir entre 2
 * portes d'entree avant meme d'avoir tape son mot de passe. Une seule URL
 * /Login, un seul champ email — le routing post-login est decide par le
 * role retourne dans la reponse.
 *
 * Compat: /CallerLogin et /ClientLogin sont des <Navigate> vers /Login
 * dans App.tsx, ?next conservé (DUP-7).
 *
 * B3-Login (W6): le bouton reste actif et les champs sont validés à l'envoi
 * (message sous le champ, focus sur le premier champ fautif), le mot de
 * passe peut s'afficher, et l'erreur dit la vraie cause (LOGIN-ERR-1) :
 * identifiants, trop de tentatives, ou panne avec le code serveur (DES-16).
 * `next` passe par safeNextPath (RUI-14).
 */
import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Mail, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/api/api-client';
import { ApiErrorException } from '@/api/client';
import { useQueryClient } from '@tanstack/react-query';
import { safeNextPath } from '@/lib/safeNextPath';
import { homeForRole } from '@/lib/homeForRole';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { AuthCard, AUTH_BUTTON_CLASS, AUTH_INPUT_CLASS, FieldError, PasswordInput, emailFieldError } from './auth/AuthCard';

interface LoginData {
  caller_name?: string;
  caller_email?: string;
  twilio_caller_id?: string;
  role?: string;
  auth?: { activeMembership?: { role?: string }; isSuperAdmin?: boolean };
}

function getRedirectTarget(role: string, isSuperAdmin: boolean): string {
  const params = new URLSearchParams(window.location.search);
  const next = safeNextPath(params.get('next'));
  if (next) return next;
  return homeForRole({ isSuperAdmin, role });
}

/** The translated cause of a failed sign-in, and the server code to show (LOGIN-ERR-1). */
export function loginFailure(err: unknown): { key: string; code?: string } {
  const apiErr = err instanceof ApiErrorException ? err : null;
  if (apiErr?.status === 401) return { key: 'admin:auth.loginError' };
  // SU-1 / ENG-13: right password, but the organisation is disabled or deleted.
  if (apiErr?.code === 'ORGANISATION_DISABLED') return { key: 'admin:loginPage.errors.organisationDisabled', code: apiErr.code };
  if (apiErr?.status === 429 || apiErr?.code === 'RATE_LIMITED') return { key: 'admin:loginPage.errors.rateLimited' };
  if (apiErr) return { key: 'admin:loginPage.errors.generic', code: apiErr.code || `HTTP_${apiErr.status}` };
  return { key: 'admin:loginPage.errors.network' };
}

export default function Login() {
  const { t } = useTranslation(['admin', 'common']);
  useDocumentTitle(t('admin:loginPage.title'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [failure, setFailure] = useState<{ key: string; code?: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Si deja connecte, redirige direct vers la bonne destination.
  useEffect(() => {
    (async () => {
      try {
        const res = await api.functions.invoke<LoginData>('callerMe', {});
        if (res?.status === 200 && res.data?.caller_email) {
          const role = res.data?.auth?.activeMembership?.role || res.data?.role || 'caller';
          const isSuper = res.data?.auth?.isSuperAdmin === true;
          window.location.href = getRedirectTarget(role, isSuper);
        }
      } catch (_e) {
        // pas connecte, laisse le formulaire s'afficher
      }
    })();
  }, []);

  // QA-W6-5: a field's error goes as soon as the user edits that field.
  const clearFieldError = (field: 'email' | 'password') =>
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    const errors = {
      email: emailFieldError(email),
      password: password ? undefined : 'admin:loginPage.errors.passwordRequired',
    };
    setFieldErrors(errors);
    setFailure(null);
    if (errors.email) { emailRef.current?.focus(); return; }
    if (errors.password) { passwordRef.current?.focus(); return; }

    setLoading(true);
    try {
      const res = await api.functions.invoke<LoginData>('loginCaller', {
        email: email.trim().toLowerCase(),
        password,
      });
      const data = res.data || {};
      const role = data.auth?.activeMembership?.role || data.role || 'caller';
      try {
        localStorage.setItem('revcall_caller', JSON.stringify({
          caller_name: data.caller_name,
          caller_email: data.caller_email,
          twilio_caller_id: data.twilio_caller_id || '',
          role,
        }));
      } catch (_e) { /* localStorage indispo */ }
      // Invalide les caches client-me et caller-me au cas ou une ancienne
      // session etait visible dans React Query.
      queryClient.removeQueries({ queryKey: ['client-me'] });
      queryClient.removeQueries({ queryKey: ['caller-me'] });
      window.location.href = getRedirectTarget(role, data.auth?.isSuperAdmin === true);
    } catch (err) {
      setFailure(loginFailure(err));
      setLoading(false);
    }
  };

  return (
    <AuthCard title={t('admin:loginPage.title')} subtitle={t('admin:loginPage.subtitle')}>
      <form onSubmit={handleLogin} noValidate className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="login-email" className="text-white/80">{t('admin:loginPage.emailLabel')}</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" aria-hidden />
            <Input
              id="login-email"
              ref={emailRef}
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); clearFieldError('email'); }}
              placeholder={t('admin:auth.emailPlaceholder')}
              className={AUTH_INPUT_CLASS}
              aria-invalid={fieldErrors.email ? true : undefined}
              aria-describedby={fieldErrors.email ? 'login-email-error' : undefined}
              autoFocus
              autoComplete="email"
            />
          </div>
          <FieldError id="login-email-error" message={fieldErrors.email && t(fieldErrors.email)} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="login-password" className="text-white/80">{t('admin:auth.password')}</Label>
          <PasswordInput
            id="login-password"
            inputRef={passwordRef}
            value={password}
            onChange={(v) => { setPassword(v); clearFieldError('password'); }}
            autoComplete="current-password"
            invalid={!!fieldErrors.password}
            describedBy={fieldErrors.password ? 'login-password-error' : undefined}
          />
          <FieldError id="login-password-error" message={fieldErrors.password && t(fieldErrors.password)} />
        </div>

        <div className="flex justify-end -mt-2">
          <Link to="/forgot-password" className="inline-flex min-h-[44px] items-center text-sm text-purple-300 hover:text-purple-200">
            {t('admin:loginPage.forgotPassword')}
          </Link>
        </div>

        {failure && (
          <div role="alert" className="space-y-1">
            <p className="text-sm text-red-400">{t(failure.key)}</p>
            {failure.code && <p className="font-vf-mono text-[11px] text-white/50">{failure.code}</p>}
          </div>
        )}

        <Button type="submit" disabled={loading} aria-busy={loading} className={AUTH_BUTTON_CLASS}>
          {loading ? <Loader2 className="w-5 h-5 animate-spin" aria-label={t('admin:auth.loggingIn')} /> : t('admin:auth.login')}
        </Button>
      </form>

      <p className="text-center text-white/60 text-sm mt-6">
        {t('admin:loginPage.notYetCustomer')}{' '}
        <a href="/#contact" className="text-purple-300 hover:text-purple-200">
          {t('admin:loginPage.contactUs')}
        </a>
      </p>
    </AuthCard>
  );
}
